import { createPortal } from 'react-dom'
import { useEffect, useMemo, useRef, useState, type PointerEvent } from 'react'
import { UiIcon } from './UiIcon'
import type { AvatarCrop } from '../avatar-image'

type AvatarCropDialogProps = {
  file: File
  busy: boolean
  onCancel: () => void
  onConfirm: (crop: AvatarCrop) => void
}

export function AvatarCropDialog({ file, busy, onCancel, onConfirm }: AvatarCropDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const dragging = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null)
  const url = useMemo(() => URL.createObjectURL(file), [file])
  const [size, setSize] = useState({ width: 0, height: 0 })
  const [center, setCenter] = useState({ x: .5, y: .5 })
  const [zoom, setZoom] = useState(1.35)

  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    return () => { dialog?.close(); URL.revokeObjectURL(url) }
  }, [url])

  const cropSide = size.width && size.height ? Math.min(size.width, size.height) / zoom : 0
  const scale = size.width && size.height ? 1 / Math.max(size.width, size.height) : 0
  const displayWidth = size.width * scale
  const displayHeight = size.height * scale
  const boxSize = cropSide * scale
  const boxLeft = (1 - displayWidth) / 2 + (center.x * size.width - cropSide / 2) * scale
  const boxTop = (1 - displayHeight) / 2 + (center.y * size.height - cropSide / 2) * scale

  const pointerInSource = (event: PointerEvent<HTMLDivElement>) => {
    const rect = previewRef.current?.getBoundingClientRect()
    if (!rect || !scale) return null
    const nx = (event.clientX - rect.left) / rect.width
    const ny = (event.clientY - rect.top) / rect.height
    return {
      x: (nx - (1 - displayWidth) / 2) / scale,
      y: (ny - (1 - displayHeight) / 2) / scale
    }
  }

  const clampCenter = (sourceX: number, sourceY: number, side = cropSide) => {
    const half = side / 2
    setCenter({
      x: Math.min(size.width - half, Math.max(half, sourceX)) / size.width,
      y: Math.min(size.height - half, Math.max(half, sourceY)) / size.height
    })
  }

  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    const point = pointerInSource(event)
    if (!point || !cropSide) return
    const centerX = center.x * size.width
    const centerY = center.y * size.height
    const half = cropSide / 2
    const inside = point.x >= centerX - half && point.x <= centerX + half && point.y >= centerY - half && point.y <= centerY + half
    if (!inside) return
    dragging.current = { pointerId: event.pointerId, offsetX: point.x - centerX, offsetY: point.y - centerY }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const moveSelection = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragging.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const point = pointerInSource(event)
    if (!point) return
    clampCenter(point.x - drag.offsetX, point.y - drag.offsetY)
  }

  const changeZoom = (nextZoom: number) => {
    const nextSide = Math.min(size.width, size.height) / nextZoom
    const half = nextSide / 2
    const currentX = center.x * size.width
    const currentY = center.y * size.height
    setZoom(nextZoom)
    if (size.width && size.height) {
      setCenter({
        x: Math.min(size.width - half, Math.max(half, currentX)) / size.width,
        y: Math.min(size.height - half, Math.max(half, currentY)) / size.height
      })
    }
  }

  return createPortal(<dialog ref={dialogRef} className="avatar-crop-dialog" aria-labelledby="avatar-crop-title" onCancel={event => { event.preventDefault(); if (!busy) onCancel() }}>
    <header>
      <div><small>头像取景</small><h2 id="avatar-crop-title">选择想保留的部分</h2></div>
      <button type="button" className="close-button" onClick={onCancel} disabled={busy} aria-label="关闭裁剪窗口"><UiIcon name="close" /></button>
    </header>
    <div
      ref={previewRef}
      className="avatar-crop-preview"
      onPointerDown={startDrag}
      onPointerMove={moveSelection}
      onPointerUp={event => { if (dragging.current?.pointerId === event.pointerId) dragging.current = null }}
      onPointerCancel={() => { dragging.current = null }}
      onLostPointerCapture={() => { dragging.current = null }}
    >
      <img src={url} alt="待裁剪头像" draggable={false} onLoad={event => setSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} />
      {Boolean(cropSide) && <span className="avatar-crop-selection" style={{ width: `${boxSize * 100}%`, height: `${boxSize * 100}%`, left: `${boxLeft * 100}%`, top: `${boxTop * 100}%` }} aria-hidden="true"><i /><i /><i /><i /></span>}
    </div>
    <label className="avatar-zoom"><span>取景范围</span><input type="range" min="1" max="4" step="0.05" value={zoom} onChange={event => changeZoom(Number(event.target.value))} disabled={busy} /><strong>{zoom.toFixed(1)}×</strong></label>
    <p>拖动选择框定位头像，缩放后确认。上传前会自动压缩。</p>
    <div className="avatar-crop-actions"><button type="button" className="secondary-button" onClick={onCancel} disabled={busy}>取消</button><button type="button" className="primary-button" onClick={() => onConfirm({ centerX: center.x, centerY: center.y, zoom })} disabled={busy || !cropSide}>{busy ? '正在压缩并上传…' : '使用这个头像'}</button></div>
  </dialog>, document.body)
}
